import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import createSafeContext from '@paradigm/utils/createSafeContext';
import { getUserId } from '@paradigm/store/src/auth/selectors';

export type TerminalTab = 'chat' | 'examples' | 'cron';

/**
 * LaunchDarkly flag that gates the entire Block Terminal feature — both
 * the bottom-panel tab and the right-rail side-panel mount.
 */
export const FEATURE_BLOCK_TERMINAL = 'feature_block_terminal';

/**
 * Where the terminal docks when open.
 *
 *   - `bottom-tab` — embedded in the BottomPanel as one of the tab views
 *     (the historical Paradigm Block default).
 *   - `side-panel` — fixed-position vertical strip on the right edge of the
 *     workspace; doesn't share space with the bottom panel.
 */
export type PinMode = 'bottom-tab' | 'side-panel';

interface Actions {
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
  readonly setPinMode: (mode: PinMode) => void;
  readonly setActiveTab: (tab: TerminalTab) => void;
}

interface State {
  readonly isOpen: boolean;
  readonly pinMode: PinMode;
  readonly activeTab: TerminalTab;
}

// `pinMode` is a per-user preference (one user prefers side-panel, another
// the bottom tab) and must not leak across users on shared machines, so we
// namespace it by userId the same way `agent-url.ts` does. `isOpen` and
// `activeTab` are transient UI state — they reset on logout and are not
// scoped by user.
const LEGACY_PIN_MODE_KEY = 'paradigm_terminal_pin_mode';
const OPEN_KEY = 'paradigm_terminal_open';
const ACTIVE_TAB_KEY = 'paradigm_terminal_active_tab';

const DEFAULT_STATE: State = {
  isOpen: false,
  pinMode: 'bottom-tab',
  activeTab: 'chat',
};

function pinModeKey(userId: string): string {
  return `${userId.toLowerCase()}:paradigm_terminal_pin_mode`;
}

function loadBoolean(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === 'true';
  } catch {
    return fallback;
  }
}

function loadPinModeForUser(userId: string | null): PinMode {
  if (userId == null) return DEFAULT_STATE.pinMode;
  try {
    const v = localStorage.getItem(pinModeKey(userId));
    if (v != null) return v === 'side-panel' ? 'side-panel' : 'bottom-tab';
    // One-shot migration from the legacy unscoped key to the per-user key,
    // so users who set their pin mode before this change don't lose it.
    const legacy = localStorage.getItem(LEGACY_PIN_MODE_KEY);
    if (legacy != null) {
      try {
        localStorage.setItem(pinModeKey(userId), legacy);
        localStorage.removeItem(LEGACY_PIN_MODE_KEY);
      } catch {
        // best-effort migration
      }
      return legacy === 'side-panel' ? 'side-panel' : 'bottom-tab';
    }
    return DEFAULT_STATE.pinMode;
  } catch {
    return DEFAULT_STATE.pinMode;
  }
}

function loadActiveTab(): TerminalTab {
  try {
    const v = localStorage.getItem(ACTIVE_TAB_KEY);
    if (v === 'examples' || v === 'cron') return v;
    return 'chat';
  } catch {
    return 'chat';
  }
}

function loadInitialState(userIdStr: string | null): State {
  if (userIdStr == null) return DEFAULT_STATE;
  return {
    isOpen: loadBoolean(OPEN_KEY, false),
    pinMode: loadPinModeForUser(userIdStr),
    activeTab: loadActiveTab(),
  };
}

export const [useTerminalDrawerState, StateProvider] = createSafeContext<State>(
  'TerminalDrawerState',
);

export const [useTerminalDrawerActions, ActionsProvider] =
  createSafeContext<Actions>('TerminalDrawerActions');

interface Props {
  readonly children: ReactNode;
}

export function TerminalDrawerProvider({ children }: Props) {
  const userId = useSelector(getUserId);
  const userIdStr =
    userId != null && String(userId).length > 0 ? String(userId) : null;

  const [state, setState] = useState<State>(() => loadInitialState(userIdStr));

  const actions = useMemo(() => createActions(setState), []);

  // Hydrate from per-user storage on login, wipe transient state on logout.
  // `hydratedForRef` makes this a one-shot per user — the persistence
  // effects below own steady-state writes.
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (userIdStr == null) {
      // Transient UI state must not survive sign-out: another user on the
      // same device should land on default layout, not the previous user's
      // open drawer / active tab.
      setState(DEFAULT_STATE);
      try {
        localStorage.removeItem(OPEN_KEY);
        localStorage.removeItem(ACTIVE_TAB_KEY);
      } catch {
        // storage unavailable — ignore
      }
      hydratedForRef.current = null;
      return;
    }
    if (hydratedForRef.current === userIdStr) return;
    setState({
      isOpen: loadBoolean(OPEN_KEY, false),
      pinMode: loadPinModeForUser(userIdStr),
      activeTab: loadActiveTab(),
    });
    hydratedForRef.current = userIdStr;
  }, [userIdStr]);

  // Skip persistence while unauthenticated so the logout wipe above isn't
  // immediately re-created by the effects writing default values back.
  useEffect(() => {
    if (userIdStr == null) return;
    try {
      localStorage.setItem(OPEN_KEY, String(state.isOpen));
    } catch {
      // storage unavailable (private mode) — ignore
    }
  }, [userIdStr, state.isOpen]);

  useEffect(() => {
    if (userIdStr == null) return;
    try {
      localStorage.setItem(pinModeKey(userIdStr), state.pinMode);
    } catch {
      // ignore
    }
  }, [userIdStr, state.pinMode]);

  useEffect(() => {
    if (userIdStr == null) return;
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, state.activeTab);
    } catch {
      // ignore
    }
  }, [userIdStr, state.activeTab]);

  // Toggle drawer with ` (backtick/tilde). Cmd/Ctrl+` works from anywhere
  // including inputs, so users can close while typing in chat. Plain `
  // skips editable targets to avoid hijacking literal backticks.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== '`' && e.key !== '~') return;
      if (e.altKey) return;
      const withModifier = e.metaKey || e.ctrlKey;
      if (!withModifier) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target?.isContentEditable === true;
        if (isEditable) return;
      }
      e.preventDefault();
      actions.toggle();
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [actions]);

  return (
    <StateProvider value={state}>
      <ActionsProvider value={actions}>{children}</ActionsProvider>
    </StateProvider>
  );
}

function createActions(setState: Dispatch<SetStateAction<State>>): Actions {
  return {
    open() {
      setState((prev) => ({ ...prev, isOpen: true }));
    },
    close() {
      setState((prev) => ({ ...prev, isOpen: false }));
    },
    toggle() {
      setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
    },
    setPinMode(mode: PinMode) {
      setState((prev) => ({ ...prev, pinMode: mode }));
    },
    setActiveTab(tab: TerminalTab) {
      setState((prev) => ({ ...prev, activeTab: tab }));
    },
  };
}
